/*
 * CheckboxField.tsx
 * Composant de case à cocher avec label, gestion des erreurs et styles personnalisés.
 * @param onClick - Fonction de rappel à exécuter lors du clic sur la case à cocher.
 * @returns Le composant CheckboxField.
 */

import React from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { Skeleton } from '@radix-ui/themes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import '../../styles/global.css';

// Définition des props du composant CheckboxField
interface CheckboxFieldProps {
	label: string;
	checked: boolean;
	onChange: (checked: boolean) => void;
	size?: 'sm' | 'md' | 'lg';
	loading?: boolean;
}

// Composant CheckboxField
const CheckboxField: React.FC<CheckboxFieldProps> = ({
	label,
	checked,
	onChange,
	size = 'md',
	loading,
}) => {
	return (
		<label className="flex items-center space-x-2 cursor-pointer select-none">
			{loading ? (
				<Skeleton
					className={`
                        h-${size === 'sm' ? '4' : size === 'lg' ? '6' : '5'}
                        w-${size === 'sm' ? '4' : size === 'lg' ? '6' : '5'}
                        rounded-sm border-2 block bg-gradient-to-l from-gray-200 via-gray-100 to-gray-200
                        `}
					style={{
						backgroundSize: '200% 100%',
						animation: 'shimmer 1.5s infinite linear',
					}}
				/>
			) : (
				<Checkbox.Root
					checked={checked}
					onCheckedChange={(val) => onChange(Boolean(val))}
					className={`
						${size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5'}
                    flex items-center justify-center rounded-sm border-2
					transition-colors duration-150 ease-in-out
					${checked ? 'bg-blue-600 border-blue-600' : 'bg-gray-200 border-gray-400 hover:border-blue-400'}
					focus:outline-none focus:ring-2 focus:ring-blue-200
                    
				`}
				>
					<Checkbox.Indicator>
						<FontAwesomeIcon
							icon={faCheck}
							className={`
                        ${size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}
                        text-white
                    `}
						/>
					</Checkbox.Indicator>
				</Checkbox.Root>
			)}
			<span className="block text-sm font-medium text-gray-700">{label}</span>
		</label>
	);
};

export default CheckboxField;
